const DEFAULT_FILE_NAME = 'file';
const DEFAULT_MIME = 'application/octet-stream';
// Long enough for the browser to start reading the blob before the object URL goes away.
const REVOKE_DELAY_MS = 30_000;

const EXTENSION_BY_MIME: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/json': 'json',
    'application/zip': 'zip',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'text/plain': 'txt',
    'text/csv': 'csv',
    'text/html': 'html',
};

// Leading base64 characters of well-known file signatures, used when the value comes without a data URL prefix.
const MIME_BY_BASE64_SIGNATURE: [string, string][] = [
    ['JVBERi0', 'application/pdf'],
    ['iVBORw0KGgo', 'image/png'],
    ['/9j/', 'image/jpeg'],
    ['R0lGOD', 'image/gif'],
    ['UEsDB', 'application/zip'],
];

const BASE64_REGEXP = /^[A-Za-z0-9+/]+={0,2}$/;
const EXTENSION_REGEXP = /\.[a-z0-9]{1,8}$/i;

export type TDataUrl = {
    mime: string;
    name?: string;
    isBase64: boolean;
    payload: string;
};

/** `decodeURIComponent` that returns the value as-is instead of throwing on malformed escapes. */
export const safeDecodeURIComponent = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

/** Parses `data:[<mime>][;name=<file>][;base64],<payload>`; returns `null` for anything else. */
export const parseDataUrl = (value: string): TDataUrl | null => {
    const match = /^data:([^,]*),(.*)$/is.exec(value.trim());
    if (!match) {
        return null;
    }
    const [mimePart = '', ...params] = match[1].split(';').map((part) => part.trim());
    let name: string | undefined;
    let isBase64 = false;
    for (const param of params) {
        const [key, ...rest] = param.split('=');
        const paramName = key.trim().toLowerCase();
        if (paramName === 'base64' && rest.length === 0) {
            isBase64 = true;
        } else if ((paramName === 'name' || paramName === 'filename') && rest.length > 0) {
            name = safeDecodeURIComponent(rest.join('=').replace(/^"|"$/g, ''));
        }
    }
    return { mime: mimePart.toLowerCase() || DEFAULT_MIME, name: name || undefined, isBase64, payload: match[2] };
};

const getExtensionByMime = (mime: string): string | undefined => EXTENSION_BY_MIME[mime.toLowerCase()];

export const getMimeByBase64Signature = (base64: string): string =>
    MIME_BY_BASE64_SIGNATURE.find(([signature]) => base64.startsWith(signature))?.[1] ?? DEFAULT_MIME;

/** Uses the given name, appending an extension derived from the mime type when the name has none. */
export const resolveFileName = (mime: string, name?: string): string => {
    const baseName = name?.trim() || DEFAULT_FILE_NAME;
    const extension = getExtensionByMime(mime);
    return extension && !EXTENSION_REGEXP.test(baseName) ? `${baseName}.${extension}` : baseName;
};

/** Strips whitespace and maps the URL-safe alphabet to the standard one; returns `null` when it is not base64. */
export const normalizeBase64 = (value: string): string | null => {
    const normalized = value.replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/');
    return normalized.length > 0 && BASE64_REGEXP.test(normalized) ? normalized : null;
};

/** Clicks a temporary `<a download>` for the given href; without a name the browser picks one. */
export const triggerDownload = (href: string, name?: string): void => {
    const link = document.createElement('a');
    link.href = href;
    if (name) {
        link.download = name;
    }
    document.body.appendChild(link);
    link.click();
    link.remove();
};

/** Saves a blob as a file, revoking its object URL once the browser has had time to read it. */
export const saveBlob = (blob: Blob, name?: string): void => {
    const href = URL.createObjectURL(blob);
    triggerDownload(href, name);
    setTimeout(() => URL.revokeObjectURL(href), REVOKE_DELAY_MS);
};
